/**
 * Phase 2 stub — rendered when the user reaches a disabled view directly
 * (e.g. via a stale link in a browser session). The navigation UI uses
 * `aria-disabled` + tooltips to keep this from happening through normal
 * interaction, but the router must resolve every view enum member.
 *
 * Copy stays in lockstep with the disabled-tab tooltip ("Available in
 * Phase 2 — Transactional mode") so the message is consistent across
 * dashboard, MobileDrawer, and direct navigation.
 */

import { ArrowRight } from 'lucide-react';

import type { View } from '../../app/view-router.js';
import { useC } from '../../lib/theme.js';
import { NumLabel } from '../../components/atoms/index.js';

const serifFont = "'Instrument Serif', serif";
const sansFont = "'Geist', sans-serif";

export interface Phase2StubViewProps {
  setView: (v: View) => void;
  reason?: 'no_active_site' | 'account_settings_stub';
}

export function Phase2StubView({ setView, reason }: Phase2StubViewProps): JSX.Element {
  const C = useC();

  const title =
    reason === 'no_active_site'
      ? 'No active site'
      : reason === 'account_settings_stub'
        ? 'Account settings'
        : 'Available in Phase 2';

  const body =
    reason === 'no_active_site'
      ? 'Pick a site from the menu, or add a new one to continue.'
      : reason === 'account_settings_stub'
        ? 'Profile, security, and notifications ship with the multi-tenant work in Phase 2.'
        : 'Transactional mode — signed mandates, policy engine, payment authorization, and dispute resolution — ships in Phase 2.';

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-8 py-16">
      <NumLabel n={0}>Phase 2 · Transactional mode</NumLabel>
      <h1
        style={{
          fontFamily: serifFont,
          fontSize: 'clamp(40px, 7vw, 64px)',
          lineHeight: 1,
          color: C.text,
          letterSpacing: -2,
          marginBottom: 16,
        }}
      >
        {title}
      </h1>
      <p
        style={{
          fontFamily: sansFont,
          fontSize: 16,
          color: C.muted,
          lineHeight: 1.6,
          marginBottom: 32,
          maxWidth: 540,
        }}
      >
        {body}
      </p>
      <button
        type="button"
        onClick={() => setView('sites')}
        className="inline-flex items-center gap-2 px-5 py-2.5"
        style={{
          background: C.lime,
          color: C.ink,
          fontFamily: sansFont,
          fontWeight: 600,
          fontSize: 14,
          borderRadius: 2,
        }}
      >
        Back to Sites <ArrowRight size={14} strokeWidth={2.5} />
      </button>
    </div>
  );
}
